import { UiStringsProvider, type UiStringsInput } from "@seagames/ui";
import { type ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { intlLocaleFor } from "./locales";

/**
 * Feeds `@seagames/ui` its chrome (table, pagination, select, filter bar,
 * modal) from `common:ui.*` — once, for the whole app, so no list page has to
 * remember to pass "Filter" / "Rows per page" / "Search…" itself. The same
 * shape as GMS's `apps/gms/src/lib/i18n/ui-strings.tsx`.
 *
 * `useTranslation` re-renders this on a language change; the object is
 * memoised on the language so the tree below re-renders only then. Numbers go
 * through `intlLocaleFor()` — "bm" must never reach Intl (it is Bambara there).
 */
export function OvrUiStrings({ children }: { children: ReactNode }) {
	const { t, i18n } = useTranslation("common");
	const language = i18n.language;

	const value = useMemo<UiStringsInput>(() => {
		const nf = new Intl.NumberFormat(intlLocaleFor(language));
		const fmt = (n: number) => nf.format(n);
		return {
			formatNumber: fmt,
			dataTable: {
				actionHeader: t("ui.dataTable.action"),
				errorTitle: t("ui.dataTable.errorTitle"),
				retry: t("ui.dataTable.tryAgain"),
				emptyTitle: t("ui.dataTable.emptyTitle"),
				selected: t("ui.dataTable.selected"),
				onOtherPages: (count) => t("ui.dataTable.onOtherPages", { n: fmt(count) }),
				clearSelection: t("ui.dataTable.clear"),
				columns: t("ui.dataTable.columns"),
				showColumns: t("ui.dataTable.showColumns"),
				columnAlways: t("ui.dataTable.always"),
				showAllColumns: t("ui.dataTable.showAll"),
				hiddenCount: (count) => t("ui.dataTable.hiddenCount", { n: fmt(count) }),
				selectRow: (row) => t("ui.dataTable.selectRow", { row: fmt(row) }),
				selectAllRows: t("ui.dataTable.selectAllRows"),
				deselectAllRows: t("ui.dataTable.deselectAllRows"),
			},
			pagination: {
				label: t("ui.pagination.label"),
				summary: ({ from, to, total }) =>
					t("ui.pagination.summary", { count: total, from: fmt(from), to: fmt(to), total: fmt(total) }),
				empty: t("ui.pagination.empty"),
				rowsPerPage: t("ui.pagination.rowsPerPage"),
				firstPage: t("ui.pagination.firstPage"),
				previousPage: t("ui.pagination.previousPage"),
				nextPage: t("ui.pagination.nextPage"),
				lastPage: t("ui.pagination.lastPage"),
				page: (page) => t("ui.pagination.page", { page: fmt(page) }),
				summaryWithNoun: ({ from, to, total, noun }) =>
					t("ui.pagination.summaryWithNoun", { from: fmt(from), to: fmt(to), total: fmt(total), noun }),
				emptyWithNoun: (noun) => t("ui.pagination.emptyWithNoun", { noun }),
			},
			select: {
				placeholder: t("ui.select.placeholder"),
				searchPlaceholder: t("ui.select.searchPlaceholder"),
				noMatches: t("ui.select.noMatches"),
				clear: t("ui.select.clear"),
				removeOption: (label) => t("ui.select.removeOption", { label }),
				selectedCount: (count) => t("ui.select.selectedCount", { n: fmt(count) }),
				selectAll: t("ui.select.selectAll"),
			},
			filterBar: { label: t("ui.filterBar.label"), apply: t("ui.filterBar.apply"), reset: t("ui.filterBar.reset") },
			modal: { close: t("ui.modal.close") },
		};
	}, [t, language]);

	return <UiStringsProvider value={value}>{children}</UiStringsProvider>;
}
